import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShieldCheck, ArrowLeft, ArrowRight, CheckCircle2, Phone, Check,
} from 'lucide-react';
import apiPublico from '../api/axiosPublico';
import './Landing.css';
import './Solicitar.css';

/**
 * Solicitud de alta: el registro de una residencial nueva.
 *
 * No crea nada por sí sola —la activa una persona— y el formulario lo dice
 * con todas sus letras. Prometer «su sistema queda listo al instante» y luego
 * hacer esperar una llamada es la forma más barata de perder al cliente en el
 * primer minuto.
 *
 * Usa el cliente PÚBLICO: quien llena esto no tiene sesión, y el cliente
 * normal lo mandaría a la pantalla de acceso ante cualquier error.
 */

const WHATSAPP = 'https://wa.me/50368960231';

const CAMPOS = [
  {
    nombre: 'residencial',
    etiqueta: 'Nombre de la residencial',
    requerido: true,
    // Genérico a propósito: este formulario es público y el nombre de una
    // residencial real no tiene por qué aparecer en él.
    placeholder: 'Residencial Los Pinos',
    autoComplete: 'organization',
  },
  {
    nombre: 'contacto',
    etiqueta: 'Su nombre',
    requerido: true,
    placeholder: 'Nombre y apellido',
    autoComplete: 'name',
  },
  {
    nombre: 'email',
    etiqueta: 'Correo',
    requerido: true,
    tipo: 'email',
    placeholder: 'directiva@suresidencial.com',
    autoComplete: 'email',
  },
  {
    nombre: 'telefono',
    etiqueta: 'Teléfono o WhatsApp',
    tipo: 'tel',
    placeholder: '7000-0000',
    autoComplete: 'tel',
  },
  {
    nombre: 'casas',
    etiqueta: 'Cuántas casas tiene',
    tipo: 'number',
    placeholder: '120',
    min: 0,
  },
];

const LO_QUE_INCLUYE = [
  'El registro de visitas completo, sin cuadernos',
  'El residente manda el pase por WhatsApp',
  'Entrada y salida con un toque, o escaneando',
  'Aviso al residente cuando su visita llega',
  'Reportes en Excel y PDF para la directiva',
  'Sus datos protegidos y aparte de otras comunidades',
];

function Solicitar() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    residencial: '',
    contacto: '',
    email: '',
    telefono: '',
    casas: '',
    mensaje: '',
  });
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState('');

  const cambiar = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  const enviar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    setError('');
    try {
      await apiPublico.post('/solicitudes', {
        ...form,
        casas: form.casas ? Number(form.casas) : undefined,
      });
      setListo(true);
    } catch (err) {
      const datos = err.response?.data;
      setError(
        datos?.errors?.[0]?.msg ||
          datos?.message ||
          'No se pudo enviar la solicitud. Intente de nuevo o escríbanos por WhatsApp.'
      );
    } finally {
      setEnviando(false);
    }
  };

  if (listo) {
    return (
      <div className="page-landing page-solicitar">
        <div className="container-landing solicitar-listo">
          <span className="solicitar-listo-icono" aria-hidden="true">
            <CheckCircle2 />
          </span>
          <h1 className="section-title-landing">Solicitud recibida</h1>
          <p className="section-text-landing">
            Le escribiremos a <strong>{form.email}</strong> para coordinar la activación de{' '}
            <strong>{form.residencial}</strong>. Si prefiere, escríbanos por WhatsApp y lo
            resolvemos hoy mismo.
          </p>
          <div className="solicitar-listo-acciones">
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="btn-landing btn-primary-landing">
              <Phone /> Escribir por WhatsApp
            </a>
            <button type="button" className="btn-landing btn-outline-landing" onClick={() => navigate('/')}>
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-landing page-solicitar">
      <header className="navbar-landing scrolled-landing">
        <div className="container-landing navbar-inner-landing">
          <Link to="/" className="brand-landing">
            <ShieldCheck className="brand-icon-landing" strokeWidth={2.25} aria-hidden="true" />
            <span>Control Residencial</span>
          </Link>
          <Link to="/" className="btn-landing btn-outline-landing solicitar-volver">
            <ArrowLeft /> Volver
          </Link>
        </div>
      </header>

      <main className="container-landing solicitar-grid">
        <section className="solicitar-copy">
          <p className="eyebrow-landing">Solicitud de alta</p>
          <h1 className="section-title-landing">Cuéntenos de su residencial</h1>
          <p className="section-text-landing">
            Con estos datos creamos su residencial y la cuenta de su administrador, y le
            entregamos el acceso. Desde ahí la administración da de alta a residentes y guardias.
          </p>

          <ul className="checks-landing">
            {LO_QUE_INCLUYE.map((t) => (
              <li key={t}><Check strokeWidth={2.5} /> {t}</li>
            ))}
          </ul>

          <p className="solicitar-nota">
            <ShieldCheck aria-hidden="true" />
            <span>
              La activación la hace una persona de ConsolTech, no un formulario automático.
              Este sistema controla quién entra a las casas de su comunidad: preferimos ese
              minuto de más.
            </span>
          </p>
        </section>

        <section className="solicitar-form-caja">
          <form className="solicitar-form" onSubmit={enviar}>
            {CAMPOS.map((c) => (
              <label key={c.nombre} className="solicitar-campo">
                <span className="solicitar-etiqueta">
                  {c.etiqueta}
                  {c.requerido && <em aria-hidden="true"> *</em>}
                </span>
                <input
                  className="solicitar-input"
                  type={c.tipo || 'text'}
                  value={form[c.nombre]}
                  onChange={cambiar(c.nombre)}
                  required={c.requerido}
                  placeholder={c.placeholder}
                  autoComplete={c.autoComplete}
                  min={c.min}
                  maxLength={120}
                />
              </label>
            ))}

            <label className="solicitar-campo solicitar-campo-ancho">
              <span className="solicitar-etiqueta">¿Algo que debamos saber?</span>
              <textarea
                className="solicitar-input solicitar-textarea"
                value={form.mensaje}
                onChange={cambiar('mensaje')}
                rows={3}
                maxLength={1000}
                placeholder="Cuántas casetas tiene, si ya usan algún sistema, cuándo quieren empezar…"
              />
            </label>

            {error && <p className="solicitar-error" role="alert">{error}</p>}

            <button
              type="submit"
              className="btn-landing btn-primary-landing btn-block-landing"
              disabled={enviando}
            >
              {enviando ? 'Enviando…' : 'Enviar solicitud'} <ArrowRight />
            </button>

            <p className="solicitar-legal">
              Usamos sus datos solo para contactarle sobre esta solicitud.
            </p>
          </form>
        </section>
      </main>
    </div>
  );
}

export default Solicitar;
